import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { toast } from 'react-hot-toast';

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: ReactNode;
}

interface RealTimeEvent {
  type: 'job_posted' | 'application_submitted' | 'score_updated' | 'ranking_changed';
  data: any;
  timestamp: Date;
  userId?: string;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { currentUser, getToken } = useAuth();

  useEffect(() => {
    if (currentUser) {
      // Initialize socket connection
      // Remove /api from the URL for WebSocket connection
      let socketUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      if (socketUrl.endsWith('/api')) {
        socketUrl = socketUrl.replace('/api', '');
      }
      
      // Handle production environment
      if (process.env.NODE_ENV === 'production' && socketUrl === '/') {
        socketUrl = window.location.origin;
      }
      
      console.log('Socket connecting to:', socketUrl);
      
      const newSocket = io(socketUrl, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 5,
        timeout: 10000,
      });

      newSocket.on('connect', async () => {
        console.log('Socket connected');
        setIsConnected(true);
        
        // Authenticate socket connection
        try {
          const token = await getToken();
          if (token) {
            newSocket.emit('authenticate', token);
          }
        } catch (error) {
          console.error('Socket authentication failed:', error);
        }
      });

      newSocket.on('disconnect', () => {
        console.log('Socket disconnected');
        setIsConnected(false);
      });

      newSocket.on('authenticated', (data) => {
        console.log('Socket authenticated:', data);
      });

      newSocket.on('auth_error', (error) => {
        console.error('Socket auth error:', error);
        toast.error('Real-time connection failed');
      });

      // Handle real-time events
      newSocket.on('real_time_event', (event: RealTimeEvent) => {
        handleRealTimeEvent(event);
      });

      setSocket(newSocket);

      return () => {
        newSocket.close();
        setSocket(null);
        setIsConnected(false);
      };
    }
  }, [currentUser, getToken]);

  const handleRealTimeEvent = (event: RealTimeEvent) => {
    switch (event.type) {
      case 'job_posted':
        toast.success(`New job posted: ${event.data.title}`, {
          duration: 4000,
        });
        break;
      
      case 'application_submitted':
        toast.success('New job application received', {
          duration: 4000,
        });
        break;
      
      case 'score_updated':
        toast.success('Your score has been updated!', {
          duration: 3000,
        });
        break;
      
      case 'ranking_changed':
        toast.success('Your ranking has changed!', {
          duration: 3000,
        });
        break;
      
      default:
        console.log('Unknown real-time event:', event);
    }
  };

  const joinRoom = (room: string) => {
    if (socket) {
      socket.emit('join_room', room);
    }
  };

  const leaveRoom = (room: string) => {
    if (socket) {
      socket.emit('leave_room', room);
    }
  };

  const value: SocketContextType = {
    socket,
    isConnected,
    joinRoom,
    leaveRoom,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}; 